import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Button,
  IconButton,
  Drawer,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import { fetchFiltersRequest } from "../../redux/api/apiSlice";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "@mui/material";
import Grid2 from "@mui/material/Grid2"; // Import Grid2
import styles from "./FilterSidebar.module.css";

const FilterSidebar = ({
  dataset,
  selectedFilters,
  onFilterChange,
  onResetFilters,
}) => {
  const dispatch = useDispatch();
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const { filters, loading, error } = useSelector((state) => state.api); // Extract state from Redux store
  const specificFilters = filters[dataset] || {};
  const specificSelectedFilters = selectedFilters[dataset] || {};
  const isMobile = useMediaQuery("(max-width:768px)");

  // Fetch filters when the component is mounted or the dataset changes
  useEffect(() => {
    if (!Object.keys(specificFilters).length) {
      dispatch(fetchFiltersRequest(dataset));
    }
  }, [dispatch, specificFilters, dataset]);

  const renderFilters = () => (
    <>
      {/* Reset all filters */}
      <Button
        className={styles.resetButton}
        onClick={() => onResetFilters(dataset)}
        sx={{
          backgroundColor: "secondary.main",
          color: "other.main",
          "&:hover": {
            backgroundColor: "secondary.main",
          },
        }}
      >
        Reset Filters
      </Button>

      {/* Map through filter categories and render checkboxes */}
      {Object.keys(specificFilters).map((category) => (
        <Accordion
          key={category}
          className={styles.accordion}
          defaultExpanded
          sx={{
            color: "primary.main",
            backgroundColor: "inherit",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
            aria-controls={`${category}-content`}
            id={`${category}-header`}
          >
            <Typography
              className={styles.filterCategoryName}
              sx={{ color: "primary.main" }}
            >
              {category}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className={styles.filterCheckbox}>
              {specificFilters[category].map((value, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={
                        specificSelectedFilters[category]?.includes(value) ||
                        false
                      }
                      onChange={() => onFilterChange(category, value, dataset)}
                      sx={{
                        color: "secondary.main",
                      }}
                    />
                  }
                  label={value}
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            onClick={() => setOverlayOpen(true)}
            sx={{
              backgroundColor: "secondary.main",
              color: "primary.main",
              position: "fixed",
              bottom: 16,
              right: 16,
              zIndex: 1000,
              "&:hover": {
                backgroundColor: "secondary.main",
              },
            }}
          >
            <FilterListIcon />
          </IconButton>
          <Drawer
            anchor="left"
            open={isOverlayOpen}
            onClose={() => setOverlayOpen(false)}
            PaperProps={{
              sx: {
                width: "40%",
                backgroundColor: "other.main",
                color: "primary.main",
              },
            }}
          >
            <Box>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : error ? (
                <Typography color="error">Error: {error}</Typography>
              ) : (
                renderFilters()
              )}
            </Box>
          </Drawer>
        </>
      ) : (
        <Grid2
          container
          direction="row"
          spacing={3}
          className={styles.sidebar}
          sx={{ backgroundColor: "other.main", color: "primary.main" }}
        >
          <Grid2 item xs={12}>
            {loading ? (
              <Typography>Loading...</Typography>
            ) : error ? (
              <Typography color="error">Error: {error}</Typography>
            ) : (
              renderFilters()
            )}
          </Grid2>
        </Grid2>
      )}
    </>
  );
};

export default FilterSidebar;
